import { useContext, useMemo } from 'react';

import CompoundMetricsContext from '../contexts/CompoundMetricsContext';
import DatasetDefinitionsContext from '../contexts/DatasetDefinitionsContext';
import MetricsContext from '../contexts/MetricsContext';
import SpecialMetricsContext from '../contexts/SpecialMetricsContext';
import isDefined from '../isDefined';
import metricTypeCheckers from '../types/metricTypeCheckers';

const useMetricOptions = () => {
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { allMetrics } = useContext(MetricsContext);
  const { allCompoundMetrics } = useContext(CompoundMetricsContext);
  const { specialMetrics } = useContext(SpecialMetricsContext);

  const metricOptions = useMemo<Metrics.Metric[]>(
    () => [...allMetrics, ...allCompoundMetrics, ...specialMetrics],
    [allMetrics, allCompoundMetrics, specialMetrics],
  );

  const metricOptionsLookup = useMemo(() => {
    const lookup: { [metricId: string]: Metrics.Metric } = {};
    metricOptions.forEach((m) => {
      lookup[m.id] = m;
    });
    return lookup;
  }, [metricOptions]);

  const metricOptionsNoSpecials = useMemo<
    (Metrics.NormalMetric | Metrics.CompoundMetric)[]
  >(
    () => [...allMetrics, ...allCompoundMetrics],
    [allMetrics, allCompoundMetrics],
  );

  const normalMetrics = useMemo<Metrics.NormalMetric[]>(
    () => metricOptions.filter(metricTypeCheckers.isNormalMetric),
    [metricOptions],
  );

  const datasetMetricOptions = useMemo(() => {
    const result: {
      [dataset: string]: {
        metrics: Metrics.NormalMetric[];
        compoundMetrics: Metrics.CompoundMetric[];
        specialMetrics: Metrics.SpecialMetric[];
      };
    } = {};

    datasets.forEach((ds) => {
      const dsMetrics = allMetrics.filter((m) => m.dataType === ds.type);
      const dsCompounds = allCompoundMetrics.filter((cm) => {
        const internalMetrics = cm.metricIds
          .map((mid) => allMetrics.find((m) => m.id === mid))
          .filter(isDefined);
        return internalMetrics.some((m) => m.dataType === ds.type);
      });
      const dsSpecials = specialMetrics.filter((sm) => sm.dataType === ds.type);

      result[ds.type] = {
        metrics: dsMetrics,
        compoundMetrics: dsCompounds,
        specialMetrics: dsSpecials,
      };
    });

    return result;
  }, [allCompoundMetrics, allMetrics, datasets, specialMetrics]);

  return {
    metricOptions,
    metricOptionsLookup,
    metricOptionsNoSpecials,
    normalMetrics,
    datasetMetricOptions,
  };
};

export default useMetricOptions;
